import { maybe, rd } from '@passionware/monads';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentTeamName } from 'v4/entities/common/hooks/useTeam';
import { myServices } from '../../../../services/services.connected.app';
import { VendorIntegrationCard } from './VendorIntegrationCard';
import { ManualSyncButton } from './ManualSyncButton';
import { BookingTabHeader } from './BookingTabHeader';
import { useBookingTabStatus } from '../hooks/useBookingTabStatus';
import { renderError } from 'v5/patterns/feedback/ErrorAlert';

interface BookingTabContentProps {
  bookingId: number;
}

/**
 * Main booking tab component that displays visitor management information
 * for a specific booking. Shows vendor integration cards and sync controls.
 */
export const BookingTabContent: React.FC<BookingTabContentProps> = ({
  bookingId
}) => {
  const { t } = useTranslation('visitor-management');
  const teamName = useCurrentTeamName();

  // Fetch booking with vendor states
  const bookingData =
    myServices.visitorVendorStateService.useListWithBookingDetails(
      maybe.map(teamName, teamName => ({
        schema: teamName,
        filters: {
          page: 1,
          count: 1,
          booking_ids: [bookingId]
        }
      }))
    );

  // Get overall status for the booking
  const statusData = useBookingTabStatus(bookingId);

  const renderLoading = () => (
    <div className="p-6">
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 rounded w-1/3"></div>
        <div className="space-y-3">
          <div className="h-20 bg-gray-200 rounded"></div>
          <div className="h-20 bg-gray-200 rounded"></div>
        </div>
      </div>
    </div>
  );

  const renderNoVendors = () => (
    <div className="p-6 text-center">
      <div className="text-gray-500">
        {t('visitorManagement.bookingTab.noVendors')}
      </div>
    </div>
  );

  const renderNeverSynced = () => (
    <div className="p-6">
      <div className="space-y-4">
        <div className="text-center">
          <h3 className="text-sm font-medium text-gray-900 mb-2">
            {t('visitorManagement.bookingTab.neverSynced.title')}
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            {t('visitorManagement.bookingTab.neverSynced.description')}
          </p>
        </div>
        <ManualSyncButton
          bookingId={bookingId}
          variant="button"
          needsSync={true}
          className="mx-auto"
        />
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-2 w-full">
      {rd
        .journey(bookingData)
        .wait(renderLoading())
        .catch(renderError.withClassName('m-2'))
        .map(response => {
          const booking = response.results.find(
            r => r.booking_id === bookingId
          );

          // If booking not found, it means it's never been synced - show sync option
          if (!booking) {
            return renderNeverSynced();
          }

          // Don't show if no vendors configured
          const vendorStates = Object.values(booking.vendors);
          if (vendorStates.length === 0) {
            return renderNoVendors();
          }

          return rd
            .journey(statusData)
            .wait(renderLoading())
            .catch(renderError)
            .map(({ overallStatus, vendorCounts }) => (
              <div className="">
                {/* Header with booking info and manual sync */}
                <BookingTabHeader
                  booking={booking.booking}
                  overallStatus={overallStatus}
                  vendorCounts={vendorCounts}
                />

                {/* Manual sync button - prominent placement */}
                <div className="px-6 py-4">
                  <ManualSyncButton
                    bookingId={bookingId}
                    variant="button"
                    needsSync={vendorStates.some(
                      vendor => vendor?.in_sync === false
                    )}
                    className="w-full"
                  />
                </div>

                {/* Vendor integration cards */}
                <div className="px-6 pb-6 space-y-4">
                  <h3 className="text-sm font-medium text-gray-700">
                    {t('visitorManagement.bookingTab.vendorIntegrations')}
                  </h3>

                  <div className="space-y-3">
                    {Object.entries(booking.vendors).map(
                      ([vendorId, vendorState]) => (
                        <VendorIntegrationCard
                          key={vendorId}
                          vendorId={vendorId}
                          vendorState={vendorState}
                          booking={booking.booking}
                        />
                      )
                    )}
                  </div>
                </div>
              </div>
            ));
        })}
    </div>
  );
};
